import { FC, useMemo } from "react";
import styled from "@emotion/styled";
import { MDSButton, MDSButtonVariant, MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";
import { RecallPermissionKind } from "@/modules/electron/types";
import {
  ConfigurationList,
  ConfigurationListItem,
  ConfigurationListItemKind,
  ConfigurationPermissionStatus,
} from "@/components/configuration";
import {
  StepTitle,
  StepContent,
  FooterContainer,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard";
import { OnboardingStepper } from "@/components/onboarding/components/welcome-wizard/shared";

const SemiboldText = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
`;

type Props = {
  onNext: () => void;
  onSkip: () => void;
  showFinish: boolean;
  restartRequired: boolean;
  experience?: "voice-mode" | "push-to-remember";
  pushToRememberPlatform?: "mac" | "windows";
  resolveConfigurationStatus: ({
    kind,
  }: {
    kind: RecallPermissionKind;
  }) => ConfigurationPermissionStatus;
  handleEnablePermission: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
  handleOpenPermissionPane: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
};

export const OnboardingVoicePermissionsScreen: FC<Props> = ({
  onNext,
  onSkip,
  showFinish,
  restartRequired,
  experience = "voice-mode",
  pushToRememberPlatform = "mac",
  resolveConfigurationStatus,
  handleEnablePermission,
  handleOpenPermissionPane,
}) => {
  const isPushToRemember = experience === "push-to-remember";
  const requiresAccessibility = !isPushToRemember || pushToRememberPlatform === "mac";
  const microphoneStatus = resolveConfigurationStatus({ kind: RecallPermissionKind.Microphone });
  const accessibilityStatus = resolveConfigurationStatus({
    kind: RecallPermissionKind.Accessibility,
  });

  const systemAudioStatus = resolveConfigurationStatus({
    kind: RecallPermissionKind.SystemAudio,
  });

  const isMicrophoneGranted = microphoneStatus === ConfigurationPermissionStatus.Granted;
  const isAccessibilityGranted = accessibilityStatus === ConfigurationPermissionStatus.Granted;
  const isSystemAudioGranted = systemAudioStatus === ConfigurationPermissionStatus.Granted;

  const isMicrophoneLoading = microphoneStatus === ConfigurationPermissionStatus.Loading;
  const isAccessibilityLoading = accessibilityStatus === ConfigurationPermissionStatus.Loading;
  const isSystemAudioLoading = systemAudioStatus === ConfigurationPermissionStatus.Loading;

  const isAnyLoading =
    isMicrophoneLoading ||
    (requiresAccessibility && isAccessibilityLoading) ||
    (!isPushToRemember && isSystemAudioLoading);
  const allPermissionsGranted =
    isMicrophoneGranted &&
    (!requiresAccessibility || isAccessibilityGranted) &&
    (isPushToRemember || isSystemAudioGranted);

  /**
   * We let the user proceed as soon as they've requested all permissions.
   */
  const canProceed = (() => {
    if (isAnyLoading) {
      return false;
    }

    if (!isMicrophoneGranted) {
      return false;
    }

    if (requiresAccessibility && !isAccessibilityGranted) {
      return false;
    }

    if (isPushToRemember) {
      return true;
    }

    if (restartRequired) {
      return true;
    }

    return allPermissionsGranted;
  })();

  const items = useMemo<ConfigurationListItem[]>(() => {
    const microphoneItem: ConfigurationListItem = {
      kind: ConfigurationListItemKind.Permission,
      id: "microphone",
      title: "Microphone",
      description: isPushToRemember
        ? "So Mem can hear your thought while you capture—and never any other time."
        : "Mem relies on your microphone to capture your voice",
      status: resolveConfigurationStatus({ kind: RecallPermissionKind.Microphone }),
      onEnable: () =>
        handleEnablePermission({
          kind: RecallPermissionKind.Microphone,
        }),
      onOpenSystemPreferences: () =>
        handleOpenPermissionPane({
          kind: RecallPermissionKind.Microphone,
        }),
    };

    const accessibilityItem: ConfigurationListItem = {
      kind: ConfigurationListItemKind.Permission,
      id: "accessibility",
      title: "Accessibility",
      description: isPushToRemember ? (
        <>
          So Mem notices when you press <SemiboldText>Right Option</SemiboldText>—even when
          you&apos;re in another app.
        </>
      ) : (
        "Mem relies on accessibility features to start voice recording on desktop"
      ),
      status: resolveConfigurationStatus({ kind: RecallPermissionKind.Accessibility }),
      onEnable: () =>
        handleEnablePermission({
          kind: RecallPermissionKind.Accessibility,
        }),
      onOpenSystemPreferences: () =>
        handleOpenPermissionPane({
          kind: RecallPermissionKind.Accessibility,
        }),
    };

    if (isPushToRemember) {
      const itemList: ConfigurationListItem[] = [];

      if (requiresAccessibility) {
        itemList.push(accessibilityItem, {
          kind: ConfigurationListItemKind.Divider,
          id: "divider-microphone",
        });
      }

      itemList.push(microphoneItem);
      return itemList;
    }

    const itemList: ConfigurationListItem[] = [
      microphoneItem,
      {
        kind: ConfigurationListItemKind.Divider,
        id: "divider-accessibility",
      },
      accessibilityItem,
      {
        kind: ConfigurationListItemKind.Divider,
        id: "divider-screen-recording",
      },
      {
        kind: ConfigurationListItemKind.Permission,
        id: "system-audio-recording",
        title: "System audio recording only",
        description:
          "Mem relies on this permission to capture other people's voices during meetings",
        status: resolveConfigurationStatus({ kind: RecallPermissionKind.SystemAudio }),
        onEnable: () =>
          handleEnablePermission({
            kind: RecallPermissionKind.SystemAudio,
          }),
        onOpenSystemPreferences: () =>
          handleOpenPermissionPane({
            kind: RecallPermissionKind.SystemAudio,
          }),
      },
    ];

    return itemList;
  }, [
    handleEnablePermission,
    handleOpenPermissionPane,
    isPushToRemember,
    requiresAccessibility,
    resolveConfigurationStatus,
  ]);

  return (
    <WizardContentWrapper>
      <StepTitle>
        {isPushToRemember ? "Turn on quick-thought capture" : "Set up Voice Mode"}
      </StepTitle>

      <StepContent>
        <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
          {isPushToRemember ? (
            requiresAccessibility ? (
              <>
                Turn on the permissions below so you can capture a thought from anywhere on your
                Mac—hold <SemiboldText>Right Option</SemiboldText>, say it, let go. Mem never
                records unless you tell it to.
              </>
            ) : (
              "Turn this on, and you can capture a thought from anywhere on your Windows computer—hold Right Control, say it, let go. Mem never records unless you tell it to."
            )
          ) : (
            "Mem can record and create notes from meeting audio, podcasts, or simply your own voice. Grant the required permissions in your system settings."
          )}
        </MDSText>

        <ConfigurationList items={items} />
      </StepContent>

      <FooterContainer align="flex-end">
        {!isPushToRemember && <OnboardingStepper />}
        {!allPermissionsGranted && (
          <MDSButton variant={MDSButtonVariant.Transparent} onClick={onSkip}>
            {isPushToRemember
              ? "Skip — I'll type my thoughts instead"
              : "I don&apos;t want to use Voice Mode"}
          </MDSButton>
        )}
        <MDSButton variant={MDSButtonVariant.Contrast} onClick={onNext} isDisabled={!canProceed}>
          {isPushToRemember ? "Continue" : showFinish ? "Finish" : "Next"}
        </MDSButton>
      </FooterContainer>
    </WizardContentWrapper>
  );
};
